import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Textarea } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { ApiError } from "@/api/client";
import type { DedupCheckResultDto } from "@/types/counterparties";
import { BrokerageDedupModal } from "@/components/brokerages/BrokerageDedupModal";
import {
  brokeragesApi,
  type BrokerageDetailDto,
  type UpsertBrokeragePayload,
} from "@/api/brokerages";

/** WS5 — the create endpoint 409s on a Likely/Strong duplicate with the
 *  DedupCheckResultDto riding the envelope's `details` channel (the only
 *  non-2xx payload the shared client surfaces). */
function asDedupResult(err: unknown): DedupCheckResultDto | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.details as DedupCheckResultDto | undefined;
  if (!d || typeof d !== "object" || !Array.isArray(d.candidates)) return null;
  return d;
}

function toForm(b?: BrokerageDetailDto): UpsertBrokeragePayload {
  return {
    name: b?.name ?? "",
    dba: b?.dba ?? "",
    website: b?.website ?? "",
    phone: b?.phone ?? "",
    email: b?.email ?? "",
    addressLine1: b?.addressLine1 ?? "",
    city: b?.city ?? "",
    state: b?.state ?? "",
    postalCode: b?.postalCode ?? "",
    primaryContactName: b?.primaryContactName ?? "",
    notes: b?.notes ?? "",
    // Status is managed via the "Change status" control, not this form, but it
    // is carried through on edit so updating other fields doesn't reset it.
    ...(b ? { status: b.status } : {}),
  };
}

/**
 * Create / edit brokerage page. Handles both /brokerages/new and
 * /brokerages/:id/edit, mirroring the BrokerEditPage convention — a dedicated
 * page rather than a modal so the form can grow. Email and phone follow the
 * same optional-but-must-be-valid checks used on Leads and Brokers.
 */
export function BrokerageEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const editing = !!id;

  const [form, setForm] = useState<UpsertBrokeragePayload>(() => toForm());
  const [error, setError] = useState<string | null>(null);
  const [dedup, setDedup] = useState<DedupCheckResultDto | null>(null);

  const existing = useQuery({
    queryKey: ["brokerage", id],
    queryFn: () => brokeragesApi.get(id!),
    enabled: editing,
  });

  // Default breadcrumb is URL-derived, which renders the raw brokerage GUID.
  // Show the brokerage's name (linking back to the detail page) instead.
  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: "Brokerages", href: "/brokerages" },
            { label: existing.data.name || "Brokerage", href: `/brokerages/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: "Brokerages", href: "/brokerages" },
          { label: "New brokerage" },
        ],
  );

  useEffect(() => {
    if (existing.data) setForm(toForm(existing.data));
  }, [existing.data]);

  const set = <K extends keyof UpsertBrokeragePayload>(k: K, v: UpsertBrokeragePayload[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });

  const mutation = useMutation({
    mutationFn: (body: UpsertBrokeragePayload) =>
      editing ? brokeragesApi.update(id!, body) : brokeragesApi.create(body),
    onSuccess: (res) => {
      setDedup(null);
      qc.invalidateQueries({ queryKey: ["brokerages"] });
      if (editing) qc.invalidateQueries({ queryKey: ["brokerage", res.id] });
      navigate(`/brokerages/${res.id}`);
    },
    onError: (e: Error) => {
      // Create-only: a Likely/Strong duplicate 409 opens the override modal
      // instead of a raw error (the vendor create precedent).
      const dedupResult = editing ? null : asDedupResult(e);
      if (dedupResult) {
        setDedup(dedupResult);
        return;
      }
      setError(e.message);
    },
  });

  const save = (confirmCreateNew = false, createNewReason: string | null = null) => {
    setError(null);
    mutation.mutate({ ...form, name: form.name.trim(), confirmCreateNew, createNewReason });
  };

  const canSave = !!form.name.trim() && !emailError && !phoneError;

  return (
    <>
      <PageHeader
        eyebrow="Brokerages"
        title={editing ? "Edit brokerage" : "New brokerage"}
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
            <Button
              size="sm"
              loading={mutation.isPending}
              disabled={!canSave}
              onClick={() => save()}
            >
              <Save size={14} strokeWidth={1.5} aria-hidden /> Save brokerage
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Name" required className="md:col-span-2"><Input value={form.name} autoFocus onChange={(e) => set("name", e.target.value)} /></FormField>
        <FormField label="DBA"><Input value={form.dba ?? ""} onChange={(e) => set("dba", e.target.value)} /></FormField>
        <FormField label="Website"><Input value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} /></FormField>
        <FormField label="Phone" error={phoneError}>
          <Input type="tel" value={form.phone ?? ""} invalid={!!phoneError} onChange={(e) => set("phone", e.target.value)} />
        </FormField>
        <FormField label="Email" error={emailError}>
          <Input type="email" value={form.email ?? ""} invalid={!!emailError} onChange={(e) => set("email", e.target.value)} />
        </FormField>
        <FormField label="Primary contact"><Input value={form.primaryContactName ?? ""} onChange={(e) => set("primaryContactName", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3 bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Address" className="md:col-span-2"><Input value={form.addressLine1 ?? ""} onChange={(e) => set("addressLine1", e.target.value)} /></FormField>
        <FormField label="City"><Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)} /></FormField>
        <FormField label="State"><Input value={form.state ?? ""} maxLength={2} onChange={(e) => set("state", e.target.value)} /></FormField>
        <FormField label="Postal code"><Input value={form.postalCode ?? ""} onChange={(e) => set("postalCode", e.target.value)} /></FormField>
      </div>

      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mt-space-4 mb-space-2">Notes</h3>
      <div className="bg-white-pure rounded-lg border border-border-light p-space-4">
        <FormField label="Notes"><Textarea rows={4} value={form.notes ?? ""} onChange={(e) => set("notes", e.target.value)} /></FormField>
      </div>

      {error && <p className="text-caption text-status-error mt-space-3" role="alert">{error}</p>}

      {dedup ? (
        <BrokerageDedupModal
          open
          result={dedup}
          pending={mutation.isPending}
          onUseExisting={(candidate) => {
            setDedup(null);
            navigate(`/brokerages/${candidate.id}`);
          }}
          onCreateAnyway={(reason) => save(true, reason || null)}
          onCancel={() => setDedup(null)}
        />
      ) : null}
    </>
  );
}
